"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

interface ProductCategoryProps {
  data: {
    id: number;
    title?: string;
    description?: string;
    isLive: boolean;
    useH1Tag: boolean;
    Card: {
      id: number;
      title?: string;
      description?: string;
      href?: string;
      screenDisplay: boolean;
      tag?: string;
      image?: {
        data?: {
          attributes?: {
            url: string;
            alternativeText: string | null;
          };
        };
      };
    }[];
  };
}

const Layout1 = ({ data }: ProductCategoryProps) => {
  if (!data?.isLive) return null;

  const titleParts = data?.title?.split(" ") || [];
  const firstPart = titleParts[0] || "";
  const restPart = titleParts.slice(1).join(" ") || "";

  const [heroCard, ...gridCards] =
    data?.Card?.filter((c) => c?.screenDisplay) || [];

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* HERO */}
        {heroCard && (
          <div className="relative h-[450px] md:h-[550px] overflow-hidden mb-12 group">
            <Image
              src={heroCard?.image?.data?.attributes?.url || "/placeholder.jpg"}
              alt={heroCard?.title || "Hero Banner"}
              fill
              priority
              quality={85}
              sizes="100vw"
              className="object-cover transition-transform duration-1000 group-hover:scale-105"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent z-10" />

            <div className="absolute inset-0 flex items-center z-20">
              <div className="max-w-2xl px-8 md:px-16">
                <div className="text-4xl md:text-7xl font-black text-white mb-6 uppercase italic tracking-tighter leading-[0.9]">
                  {heroCard?.title?.split(" ").slice(0, -2).join(" ")} <br />
                  <span style={{ color: "var(--secondary)" }}>
                    {heroCard?.title?.split(" ").slice(-2).join(" ")}
                  </span>
                </div>

                <p className="text-lg md:text-xl text-white/80 mb-8 max-w-md font-medium leading-relaxed">
                  {heroCard?.description}
                </p>

                <Link
                  href={heroCard?.href || "#"}
                  className="inline-block px-10 py-4 font-black uppercase text-xs tracking-widest text-white transition-all duration-300 shadow-xl hover:bg-white/50 hover:text-black active:scale-95"
                  style={{ backgroundColor: "var(--secondary)" }}
                >
                  Explore Collection
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* HEADER */}
        {data?.title && (
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-8">
            <div className="space-y-4">
              <div className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-neutral-900 leading-none">
                {data.useH1Tag ? (
                  <h1>
                    {firstPart}{" "}
                    {restPart && (
                      <span
                        className="italic"
                        style={{ color: "var(--secondary)" }}
                      >
                        {restPart}
                      </span>
                    )}
                  </h1>
                ) : (
                  <h2>
                    {firstPart}{" "}
                    {restPart && (
                      <span
                        className="italic"
                        style={{ color: "var(--secondary)" }}
                      >
                        {restPart}
                      </span>
                    )}
                  </h2>
                )}
              </div>
            </div>

            <Link
              href="/collections"
              className="group flex items-center gap-3 text-[11px] font-black uppercase tracking-[0.2em] text-neutral-900 border-b-2 border-neutral-900 pb-1 transition-colors hover:text-[var(--secondary)] hover:border-[var(--secondary)] w-fit"
            >
              Explore All
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        )}

        {/* GRID */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
          {gridCards.map((card, index) => (
            <div
              key={card?.id || index}
              className="group relative overflow-hidden bg-neutral-100 cursor-pointer"
            >
              <Link href={card?.href || "#"} className="block relative">
                <div className="aspect-[3/4] overflow-hidden relative">
                  <Image
                    src={
                      card?.image?.data?.attributes?.url || "/placeholder.jpg"
                    }
                    alt={card?.title || ""}
                    fill
                    quality={80}
                    sizes="(max-width: 768px) 50vw, 25vw"
                    className="object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                  />
                </div>

                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity z-10" />

                <div className="absolute inset-0 flex flex-col justify-end p-6 z-20">
                  <p
                    className="text-[10px] font-bold uppercase tracking-widest mb-1 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300"
                    style={{ color: "var(--secondary)" }}
                  >
                    {card?.description || "New Arrival"}
                  </p>

                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-black text-xl uppercase italic tracking-tighter leading-none">
                      {card?.title}
                    </h3>
                    <div className="h-8 w-8 bg-white flex items-center justify-center transform translate-x-12 group-hover:translate-x-0 transition-transform duration-300 shadow-lg">
                      <ArrowRight
                        className="h-4 w-4"
                        style={{ color: "var(--secondary)" }}
                      />
                    </div>
                  </div>

                  <div
                    className="mt-4 w-0 h-1 transition-all duration-300 group-hover:w-full"
                    style={{ backgroundColor: "var(--secondary)" }}
                  />
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Layout1;
